import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { usePromise } from '../../../hooks/usePromise/usePromise';
import { getUserTxs } from '../../../services/scanService';
import { getContractAddress } from '../../../services/contractReader';
import ExternalLink from '../Icons/ProviderIcons/ExternalLink';
import ShowAt from '../ShowAt/ShowAt';
import { compareObjects } from '../../../utils/validation';

export const compareSearch = (condition, value) => {
  if (!condition || !value) return;

  return String(condition)?.toLowerCase?.() === String(value)?.toLowerCase?.() || condition === value;
};

function TxFinder({ account, scan, condition, children, isMultible, extraConfig, searchFunc, onFound }) {
  const [, hpoolManagerAddress] = usePromise(() => getContractAddress('HordHpoolManager'), { initReq: true });
  const [found, setFound] = useState(isMultible ? [] : {});
  const [searchIsEnded, setSearchIsEnded] = useState(true);

  const searchMethod = isMultible ? 'filter' : 'find';
  const isEveryMethod = isMultible ? 'some' : 'every';

  const txViewUrl = `${scan}tx/${found?.hash}`;

  const [page, setPape] = useState(1);
  const nextPage = () => setPape((p) => p + 1);
  const startSearching = () => setSearchIsEnded(false);
  const stopSearching = () => setSearchIsEnded(true);

  const mergedConditions = { ...condition, to: hpoolManagerAddress, from: account, ...extraConfig };

  const [getTxs, { result: txs = [] } = {}, txLoading, error] = usePromise(getUserTxs);
  const foundValidValue = Array.isArray(found) ? found?.length : Object.values(found || {});
  const loading = !searchIsEnded || (txLoading && !error);

  useEffect(() => {
    if (!account || !scan || !hpoolManagerAddress) return;
    startSearching();
    getTxs(account, scan, { page, offset: 40, ...mergedConditions }).finally(stopSearching);
  }, [account, page, hpoolManagerAddress]);

  const findTx = (_txs) => {
    const found = _txs?.[searchMethod]((tx) => {
      if (searchFunc) return searchFunc(tx);
      const match = Object.entries(mergedConditions)?.[isEveryMethod](([key, value]) => {
        const txValue = tx?.[key];
        const searchValue = value;
        // handle array values
        if (Array.isArray(searchValue)) {
          return searchValue.find((_search) => compareSearch(_search, txValue));
        }
        return compareSearch(searchValue, txValue);
      });
      return match;
    });
    return found;
  };

  // const didNotExceedStopSearchDate = (tx = {}) => {
  //   let { timeStamp: txTimeStamp = 0 } = tx;
  //   txTimeStamp *= 1000;
  //   if (txTimeStamp - stopSearchDate < 0 || !txTimeStamp) {
  //     throw new Error('this tx is beyond the stop search date');
  //   }
  // };

  const updateFoundTx = () => {
    const validatedTxs = Array.isArray(txs) ? txs : [];
    const _found = findTx(validatedTxs);
    setSearchIsEnded(true);

    if (isMultible) {
      setFound([...found, ..._found]);
      return;
    }
    if (!_found) nextPage();
    else setFound(_found);
  };

  useEffect(() => {
    if (!txs.length) return;
    try {
      updateFoundTx();
    } catch (error) {
      console.log();
    }
  }, [txs?.[0]]);

  useEffect(() => {
    if (!foundValidValue) return;
    onFound(found);
  }, [found]);

  return (
    <>
      <ShowAt at={!children && !!found?.hash}>
        <a href={txViewUrl} target="_blank" rel="noreferrer">
          <ExternalLink />
        </a>
      </ShowAt>
      {children?.({ found, loading: !searchIsEnded })}
    </>
  );
}

const mapStateToProps = ({ general, wallet }) => ({
  account: wallet.account,
  scan: general.scan,
});

TxFinder.defaultProps = {
  condition: {
    from: '',
  },
  children: undefined,
  searchFunc: undefined,
  isMultible: false,
  extraConfig: {},
  onFound: () => {},
};
TxFinder.propTypes = {
  account: PropTypes.string.isRequired,
  scan: PropTypes.string.isRequired,
  condition: PropTypes.object,
  isMultible: PropTypes.bool,
  extraConfig: PropTypes.object,
  children: PropTypes.func,
  searchFunc: PropTypes.func,
  onFound: PropTypes.func,
};

export default connect(mapStateToProps)(TxFinder);
